import type { ClientState, Playback } from '../protocol'

export interface SyncClock {
  now(): number
}

export interface StatusReport {
  seq: number
  state: ClientState
  buffered: number
  pos: number
}

const TICK_MS = 250
// Seconds buffered ahead before reporting ready while the room waits.
const READY_AHEAD = 4
// Drift handling, seconds.
const DRIFT_OK = 0.12
const DRIFT_SEEK = 1.5
const PAUSED_TOLERANCE = 0.15
// A stall must last this long before the room is paused for everyone.
const STALL_MS = 600
const REPORT_EVERY_MS = 1500

/**
 * SyncEngine keeps a video element at the room's position: it seeks on
 * jumps, nudges the playback rate to absorb small drift, and reports
 * readiness and stalls to the server.
 */
export class SyncEngine {
  private pb: Playback = { status: 'paused', pos: 0, anchor: 0, seq: 0 }
  private duration = 0
  private appliedSeq = -1
  private seeking = false
  private stalledSince = 0
  private lastTime = 0
  private lastTimeAt = 0
  private lastReport: StatusReport | null = null
  private lastReportAt = 0
  private timer = 0
  private enabled = false
  private onAutoplayBlocked?: () => void

  constructor(
    private readonly video: HTMLVideoElement,
    private readonly clock: SyncClock,
    private readonly report: (s: StatusReport) => void,
  ) {
    video.addEventListener('seeking', this.onSeeking)
    video.addEventListener('seeked', this.onSeeked)
    video.addEventListener('waiting', this.onWaiting)
    video.addEventListener('playing', this.onPlaying)
    video.addEventListener('canplay', this.onPlaying)
    this.timer = window.setInterval(this.tick, TICK_MS)
  }

  destroy() {
    window.clearInterval(this.timer)
    this.video.removeEventListener('seeking', this.onSeeking)
    this.video.removeEventListener('seeked', this.onSeeked)
    this.video.removeEventListener('waiting', this.onWaiting)
    this.video.removeEventListener('playing', this.onPlaying)
    this.video.removeEventListener('canplay', this.onPlaying)
  }

  setAutoplayBlockedHandler(fn: () => void) {
    this.onAutoplayBlocked = fn
  }

  /** Enable once the media is attached; until then the client is loading. */
  setEnabled(on: boolean, duration: number) {
    this.enabled = on
    this.duration = duration
    this.appliedSeq = -1
    this.tick()
  }

  setPlayback(pb: Playback) {
    this.pb = pb
    this.tick()
  }

  /** Target position on the room timeline right now. */
  target(): number {
    const p = this.pb
    let t = p.pos
    if (p.status === 'playing') t += Math.max(0, this.clock.now() - p.anchor) / 1000
    if (this.duration > 0) t = Math.min(t, this.duration)
    return t
  }

  private onSeeking = () => {
    this.seeking = true
  }

  private onSeeked = () => {
    this.seeking = false
    this.tick()
  }

  private onWaiting = () => {
    if (!this.stalledSince) this.stalledSince = performance.now()
  }

  private onPlaying = () => {
    this.stalledSince = 0
  }

  private seekTo(t: number) {
    this.seeking = true
    this.video.currentTime = t
  }

  private bufferedAhead(t: number): number {
    const b = this.video.buffered
    for (let i = 0; i < b.length; i++) {
      // Small gaps at range starts are skipped by the player.
      if (b.start(i) <= t + 0.3 && b.end(i) >= t) return b.end(i) - t
    }
    return 0
  }

  private play() {
    const v = this.video
    v.play().catch((err: DOMException) => {
      if (err.name === 'NotAllowedError' && !v.muted) {
        // Browsers allow muted autoplay; ask the user to unmute.
        v.muted = true
        this.onAutoplayBlocked?.()
        void v.play().catch(() => {})
      }
    })
  }

  private tick = () => {
    const v = this.video
    const p = this.pb
    if (!this.enabled || !p.mediaId) {
      this.send('loading', 0)
      return
    }
    const now = this.clock.now()
    const t = this.target()
    const running = p.status === 'playing' && now >= p.anchor
    const drift = v.currentTime - t

    // Detect stalls the 'waiting' event may miss: time not advancing.
    const perf = performance.now()
    if (v.currentTime !== this.lastTime || v.paused) {
      this.lastTime = v.currentTime
      this.lastTimeAt = perf
    } else if (running && !this.seeking && perf - this.lastTimeAt > 1000 && !this.stalledSince) {
      this.stalledSince = this.lastTimeAt
    }

    if (p.seq !== this.appliedSeq) {
      this.appliedSeq = p.seq
      this.stalledSince = 0
      const limit = running ? 0.5 : PAUSED_TOLERANCE
      if (Math.abs(drift) > limit) this.seekTo(t)
    }

    if (running) {
      if (!this.seeking && Math.abs(drift) > DRIFT_SEEK) {
        this.seekTo(t)
      } else if (Math.abs(drift) > DRIFT_OK) {
        // Behind: speed up; ahead: slow down. Inaudible within a few percent.
        v.playbackRate = Math.min(1.08, Math.max(0.92, 1 - drift * 0.5))
      } else {
        v.playbackRate = 1
      }
      if (v.paused && !this.seeking) this.play()
    } else {
      if (!v.paused) v.pause()
      v.playbackRate = 1
      if (!this.seeking && Math.abs(drift) > PAUSED_TOLERANCE) this.seekTo(t)
    }

    const ahead = this.bufferedAhead(t)
    let state: ClientState
    if (running) {
      const stalled = this.stalledSince > 0 && perf - this.stalledSince > STALL_MS
      if (stalled) state = 'buffering'
      else state = Math.abs(drift) < 1 && v.readyState >= 3 ? 'ready' : 'loading'
    } else {
      const need = this.duration > 0 ? Math.min(READY_AHEAD, Math.max(0, this.duration - t - 0.5)) : READY_AHEAD
      const settled = !this.seeking && Math.abs(v.currentTime - t) < 0.5
      state = settled && ahead >= need && v.readyState >= 2 ? 'ready' : 'loading'
    }
    this.send(state, ahead)
  }

  private send(state: ClientState, buffered: number) {
    const r: StatusReport = {
      seq: this.pb.seq,
      state,
      buffered: Math.round(buffered * 10) / 10,
      pos: Math.round(this.video.currentTime * 10) / 10,
    }
    const prev = this.lastReport
    const changed = !prev || prev.seq !== r.seq || prev.state !== r.state
    if (changed || performance.now() - this.lastReportAt > REPORT_EVERY_MS) {
      this.lastReport = r
      this.lastReportAt = performance.now()
      this.report(r)
    }
  }
}
